/**
 * Resource configuration — global parameters for financial analysis.
 *
 * These are company-level values from the 2026 budget planning spreadsheet,
 * NOT per-node data. They drive calculated metrics in the finance dashboard.
 *
 * Stored in localStorage via the resource Zustand slice.
 */

/** Fixed (user-editable) resource parameters. */
export interface ResourceConfig {
  /** Annual fully-loaded cost per engineer ($). */
  avgFteCost: number;

  /** Planned IWT South subcontractor spend ($). */
  iwtSouthCommitment: number;

  /** Other subcontractor spend (VAST, Eddie, etc.) ($). */
  otherSubcontractorCommitment: number;

  /** FTE efficiency factor (0–1). Industry benchmark ~0.80. */
  fteEfficiency: number;

  /** 2026 company-wide revenue projection ($). */
  revenueProjection: number;

  /** Target R&D as percentage of revenue (0–1). */
  rndPercentOfRevenue: number;

  /** Current IWT engineering headcount applied to R&D. */
  iwtResources: number;

  /** Swim Lane investment not attributed to any project ($). */
  swimLaneInvestment: number;

  /** NPV discount rate (0–1). Default 0.10 = 10%. */
  npvDiscountRate: number;

  /** NPV analysis period in years. Default 5. */
  npvPeriodYears: number;
}

/** Calculated (derived) resource metrics — not stored, computed on read. */
export interface ResourceMetrics {
  /** IR&D budget = revenue × R&D%. */
  irdBudget: number;

  /** FTEs needed = grand total investment / (avg FTE cost × efficiency). */
  ftesNeeded: number;

  /** IWT South FTEs = IWT South commitment / avg FTE cost. */
  iwtSouthFtes: number;

  /** Other subcontractor FTEs = other commitment / avg FTE cost. */
  otherSubcontractorFtes: number;

  /** Total available = IWT resources + IWT South FTEs + other FTEs. */
  totalAvailableFtes: number;

  /** Grand total investment including swim lane ($). */
  totalInvestment: number;

  /** Resource delta = total available - FTEs needed (negative = shortfall). */
  fteDelta: number;
}

/** Default resource config from 2026 budget spreadsheet. */
export const DEFAULT_RESOURCE_CONFIG: ResourceConfig = {
  avgFteCost: 250_000,
  iwtSouthCommitment: 1_125_000,
  otherSubcontractorCommitment: 100_000,
  fteEfficiency: 0.80,
  revenueProjection: 26_900_000,
  rndPercentOfRevenue: 0.128,
  iwtResources: 12.116,
  swimLaneInvestment: 300_000,
  npvDiscountRate: 0.10,
  npvPeriodYears: 5,
};

/**
 * Compute derived resource metrics from config and total investment.
 * @param config Fixed resource parameters
 * @param grandTotalInvestment Sum of all project investments (iR&D + fR&D)
 */
export function computeResourceMetrics(
  config: ResourceConfig,
  grandTotalInvestment: number,
): ResourceMetrics {
  const irdBudget = config.revenueProjection * config.rndPercentOfRevenue;
  const totalInvestment = grandTotalInvestment + config.swimLaneInvestment;
  const effectiveCost = config.avgFteCost * config.fteEfficiency;
  const ftesNeeded = effectiveCost > 0 ? totalInvestment / effectiveCost : 0;
  const iwtSouthFtes = config.avgFteCost > 0
    ? config.iwtSouthCommitment / config.avgFteCost
    : 0;
  const otherSubcontractorFtes = config.avgFteCost > 0
    ? config.otherSubcontractorCommitment / config.avgFteCost
    : 0;
  const totalAvailableFtes = config.iwtResources + iwtSouthFtes + otherSubcontractorFtes;
  const fteDelta = totalAvailableFtes - ftesNeeded;

  return {
    irdBudget,
    totalInvestment,
    ftesNeeded,
    iwtSouthFtes,
    otherSubcontractorFtes,
    totalAvailableFtes,
    fteDelta,
  };
}
